// Persists the plugin-lifecycle DELTA from the default builtin set in
// localStorage (with the app's `slate:` prefix), so install / uninstall /
// enable / disable survive a restart. We store a delta (not the full set) so a
// fresh build still picks up newly-bundled builtins automatically.
//
// Web/SSR-safe: a missing or malformed entry reads back as "no delta" (every
// field an empty array), and a failed write simply does not persist. A future
// effort moves this to an on-disk `~/.slateeditor/` config home.

/** The persisted lifecycle delta from the default builtin set. */
export interface PluginLifecycleState {
  /** Ids currently disabled. */
  disabled: string[];
  /** Default-builtin ids the user uninstalled, so boot skips re-seeding them. */
  uninstalledBuiltins: string[];
  /** Curated ids the user installed beyond the default builtins. */
  installedExtras: string[];
}

export interface PluginStateStore {
  get(): PluginLifecycleState;
  set(s: PluginLifecycleState): void;
}

const KEY = 'slate:plugin-lifecycle';

const emptyState = (): PluginLifecycleState => ({
  disabled: [],
  uninstalledBuiltins: [],
  installedExtras: [],
});

/** Coerce an unknown field to a clean array of strings (drops non-strings). */
function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

export const localStoragePluginStateStore: PluginStateStore = {
  get() {
    try {
      const raw = localStorage.getItem(KEY);
      if (!raw) return emptyState();
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== 'object' || parsed === null) return emptyState();
      const obj = parsed as Record<string, unknown>;
      return {
        disabled: asStringArray(obj.disabled),
        uninstalledBuiltins: asStringArray(obj.uninstalledBuiltins),
        installedExtras: asStringArray(obj.installedExtras),
      };
    } catch {
      return emptyState();
    }
  },
  set(s) {
    try {
      localStorage.setItem(KEY, JSON.stringify(s));
    } catch {
      /* storage unavailable / quota — lifecycle state simply does not persist */
    }
  },
};
